import {
  FOC_INCIDENT_ROSTER_TOUR_STEPS,
  type FocIncidentRosterTourStepId,
  type FocIncidentRosterTourSurface,
} from './focIncidentRosterTourSteps';

/**
 * The tour runs outside React, so Hub / roster / settings hosts register the
 * UI moves they alone can perform.
 *
 * Cross-route navigation (roster ↔ settings) is owned by the runner handoff
 * (resume key + destroy), not by prepare/ensureSurface.
 */
export type FocIncidentRosterTourApi = {
  getLocation?: () => { pathname: string; search: string };
  /** Hub: navigate to preferred live incident roster (keeps tour param). */
  navigateToRoster: () => void;
  /** Roster: navigate to incident settings (keeps tour param / resume). */
  navigateToSettings: () => void;
  /** Roster: return from settings if needed. */
  navigateToRosterSurface: () => void;
  showTableView: () => void;
  showOrgChartView: () => void;
  expandFirstMember: () => void;
  collapseFirstMember: () => void;
  /** Focus the + Add toolbar control without opening a dialog (US-IR010). */
  openAddMember: () => void;
  openAddPosition: () => void;
  openAssignExternalUser: () => void;
  closeAddDialogs: () => void;
  openEditFirstMember: () => void;
  closeEditMember: () => void;
  ensureRosterSidebar: () => void;
  /** Org-chart template sidebar: open hurricane Full ICS load preview. */
  previewRosterLoad: () => void;
  focusRosterSearch: () => void;
  openFilters: () => void;
  closeFilters: () => void;
};

let api: Partial<FocIncidentRosterTourApi> = {};
let pendingStep: FocIncidentRosterTourStepId | null = null;

export function registerFocIncidentRosterTourApi(
  partial: Partial<FocIncidentRosterTourApi>
): () => void {
  api = { ...api, ...partial };
  if (pendingStep) {
    const step = pendingStep;
    const needed = surfaceFor(step);
    if (currentSurface() === needed) {
      pendingStep = null;
      prepareFocIncidentRosterStep(step);
    }
  }
  return () => {
    for (const key of Object.keys(
      partial
    ) as (keyof FocIncidentRosterTourApi)[]) {
      if (api[key] === partial[key]) {
        delete api[key];
      }
    }
  };
}

export function getFocIncidentRosterTourApi(): Partial<FocIncidentRosterTourApi> {
  return api;
}

function surfaceFor(
  id: FocIncidentRosterTourStepId
): FocIncidentRosterTourSurface {
  return (
    FOC_INCIDENT_ROSTER_TOUR_STEPS.find((step) => step.id === id)?.surface ??
    'roster'
  );
}

function readLocation(): { pathname: string; search: string } {
  const fromHost = getFocIncidentRosterTourApi().getLocation?.();
  if (fromHost) return fromHost;
  if (typeof window !== 'undefined' && window.location) {
    return {
      pathname: window.location.pathname,
      search: window.location.search,
    };
  }
  return { pathname: '/hub', search: '?openTab=Incidents' };
}

function currentSurface(): FocIncidentRosterTourSurface {
  const { pathname } = readLocation();
  if (pathname.includes('/settings')) return 'settings';
  if (pathname.includes('/roster')) return 'roster';
  return 'hub';
}

/**
 * Same-surface gate only. Does not navigate across hub/roster/settings —
 * route changes go through the runner handoff so a second auto-start cannot
 * race an undestroyed driver.
 */
function ensureSurface(id: FocIncidentRosterTourStepId): boolean {
  const needed = surfaceFor(id);
  const current = currentSurface();

  if (needed === current) {
    pendingStep = null;
    return true;
  }

  // Wrong surface: wait for the intentional handoff to remount the host.
  pendingStep = id;
  return false;
}

export function prepareFocIncidentRosterStep(id: FocIncidentRosterTourStepId) {
  if (!ensureSurface(id)) return;

  const host = getFocIncidentRosterTourApi();
  const surface = surfaceFor(id);

  if (surface === 'settings' || surface === 'hub') {
    return;
  }

  // roster surface
  switch (id) {
    case 'usir001':
    case 'usir002':
    case 'usir004':
    case 'usir005':
      host.closeAddDialogs?.();
      host.closeEditMember?.();
      host.closeFilters?.();
      host.showTableView?.();
      host.collapseFirstMember?.();
      return;
    case 'usir003':
    case 'usir013':
    case 'usir023':
      host.closeAddDialogs?.();
      host.closeEditMember?.();
      host.closeFilters?.();
      host.showTableView?.();
      host.expandFirstMember?.();
      return;
    case 'usir007':
    case 'usir008':
      host.closeAddDialogs?.();
      host.closeEditMember?.();
      host.closeFilters?.();
      host.showTableView?.();
      host.focusRosterSearch?.();
      return;
    case 'usir010':
      host.closeAddDialogs?.();
      host.closeEditMember?.();
      host.closeFilters?.();
      host.showTableView?.();
      host.openAddMember?.();
      return;
    case 'usir011':
      host.closeEditMember?.();
      host.closeFilters?.();
      host.showTableView?.();
      host.openAddPosition?.();
      return;
    case 'usir018':
      host.closeAddDialogs?.();
      host.closeFilters?.();
      host.showTableView?.();
      host.expandFirstMember?.();
      host.openEditFirstMember?.();
      return;
    case 'inc006a':
      host.closeAddDialogs?.();
      host.closeEditMember?.();
      host.closeFilters?.();
      host.ensureRosterSidebar?.();
      if (typeof window !== 'undefined') {
        window.setTimeout(() => host.previewRosterLoad?.(), 80);
      } else {
        host.previewRosterLoad?.();
      }
      return;
    case 'inc006c':
    case 'inc006d':
      host.closeEditMember?.();
      host.closeFilters?.();
      host.showTableView?.();
      host.openAssignExternalUser?.();
      return;
    case 'sectionIntro':
    case 'inc006':
    case 'usir015':
    case 'usir021':
    case 'usir024':
    case 'usir026':
    case 'inc006b':
    case 'focFeedback':
    default:
      host.closeAddDialogs?.();
      host.closeEditMember?.();
      host.closeFilters?.();
      host.showTableView?.();
      host.collapseFirstMember?.();
  }
}

export function resetFocIncidentRosterTourBridgeForTests() {
  api = {};
  pendingStep = null;
}
